import { addDays, format, isAfter, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Checkbox } from '@Components/FormCheck';
import { Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { useAppSelector } from '../../../redux';
import { DerivedNetwork } from '../../../redux/networks';
import { emailSelector } from '../../../redux/user';
import {
  awaitJSON,
  SheetsLambdaFetch,
  StreamingUtilsLambdaFetch,
  StreamingV2LambdaFetch
} from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

const TransferMethod = {
  EVENLY: 'Evenly',
  ALLOCATION: 'Allocation',
  ZERO: 'Zero'
} as const;

const TRANSFER_METHOD_OPTIONS: string[] = Object.values(TransferMethod);

export const TransferByMethod = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const { setError } = utils;

  const [dates, setDates] = useState<DateRange>();
  const [dateList, setDateList] = useState<string[]>([]);

  const [notes, setNotes] = useState('');

  const email = useAppSelector(emailSelector);
  const [transferMethod, setTransferMethod] = useState('');

  const [newCost, setNewCost] = useState<number | null>(null);
  const [newCount, setNewCount] = useState<number | null>(null);
  const [isImpressionChange, setIsImpressionChange] = useState(false);

  const [company, setCompany] = useState('');
  const [companyOptions, setCompanyOptions] = useState<string[]>();

  const [network, setNetwork] = useState('');
  const [derivedNetworkMap, setDerivedNetworkMap] = useState<Record<string, DerivedNetwork>>({});

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const res = await StreamingV2LambdaFetch('/derived', { params: { company } });
          const derivedNetworks = await awaitJSON<Record<string, DerivedNetwork>>(res);
          setDerivedNetworkMap(derivedNetworks);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, setError]);

  const derivedIdOptions = useMemo(() => Object.keys(derivedNetworkMap), [derivedNetworkMap]);

  const transferEvenlyOrAllocation = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch('/transferEvenlyOrAllocation', {
        method: 'POST',
        body: JSON.stringify({
          dateList,
          company,
          network,
          notes,
          email,
          newCount,
          newCost,
          transferMethod,
          isImpressionChange
        })
      });

      setCompany('');
      setNotes('');
      setDates(undefined);
      setDateList([]);
      setTransferMethod('');
      setNetwork('');
      setNewCost(null);
      setNewCount(null);
      setIsImpressionChange(false);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [
    isImpressionChange,
    dateList,
    company,
    network,
    notes,
    email,
    newCount,
    transferMethod,
    newCost,
    setError
  ]);

  return (
    <Card className='ad-ops-card' data-testid='transfer-method'>
      <div className='ad-ops-card-title'>Transfer by Method</div>
      <DatePicker
        range={dates}
        design={Design.NEUTRAL}
        isOutsideRange={() => false}
        onChange={range => {
          setDates(range);
          const datesList: string[] = [];
          const lastDate = parseISO(range.end);
          for (
            let cursor = parseISO(range.start);
            !isAfter(cursor, lastDate);
            cursor = addDays(cursor, 1)
          ) {
            datesList.push(format(cursor, 'yyyy-MM-dd'));
          }
          setDateList(datesList);
        }}
      />
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'

        menuPortalTarget={document.body}
        placeholder='Select from existing companies'
        searchable
        options={companyOptions ?? []}
        value={company || null}
        onChange={value => setCompany(value ?? '')}
      />
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Notes</InputGroup.Text>
        <Form.Control value={notes} onChange={e => setNotes(e.target.value)} />
      </InputGroup>
      {company && (
        <Dropdown
          size={Size.LG}

          placeholder='Select transfer method'
          value={transferMethod}
          options={TRANSFER_METHOD_OPTIONS}
          onChange={option => {
            setTransferMethod(option);
            if (isImpressionChange && option === TransferMethod.ZERO) {
              setNewCount(0);
            } else if (!isImpressionChange && option === TransferMethod.ZERO) {
              setNewCost(0);
            }
          }}
        />
      )}
      {transferMethod && (
        <Dropdown
          size={Size.LG}
          className='ad-ops-elem'

          menuPortalTarget={document.body}
          placeholder='Select from Derived IDs'
          searchable
          clearable
          options={derivedIdOptions}
          value={network || null}
          onChange={value => setNetwork(value ?? '')}
        />
      )}
      <div className='ad-ops-elem'>
        <Checkbox
          className='ad-ops-form-check'
          inline
          label='Total Impression Change'
          onChange={() => {
            setIsImpressionChange(!isImpressionChange);
            if (!isImpressionChange) {
              setNewCost(null);
            } else {
              setNewCount(null);
            }
            if (!isImpressionChange && transferMethod === TransferMethod.ZERO) {
              setNewCount(0);
            } else if (isImpressionChange && transferMethod === TransferMethod.ZERO) {
              setNewCost(0);
            }
          }}
        />
      </div>
      {!isImpressionChange && transferMethod !== TransferMethod.ZERO && (
        <InputGroup size='sm' className='ad-ops-elem'>
          <InputGroup.Text className='input-label'>New Cost</InputGroup.Text>
          <Form.Control
            type='number'
            value={newCost !== null ? newCost : ''}
            onChange={e => setNewCost(e.target.value !== '' ? parseFloat(e.target.value) : null)}
          />
        </InputGroup>
      )}
      {isImpressionChange && transferMethod !== TransferMethod.ZERO && (
        <InputGroup size='sm' className='ad-ops-elem'>
          <InputGroup.Text className='input-label'>New Count</InputGroup.Text>
          <Form.Control
            value={newCount !== null ? newCount : ''}
            onChange={e => setNewCount(e.target.value !== '' ? parseInt(e.target.value, 10) : null)}
          />
        </InputGroup>
      )}
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={transferEvenlyOrAllocation}
        data-testid='transfer-method-submit'
      >
        Submit
      </Button>
    </Card>
  );
};
